/**
 * DOM Context Extraction
 *
 * Extracts the actual readable content of the page — text, headings, code
 * blocks, lists — plus interactive elements (buttons, forms, links) for
 * navigation and action context.
 */

import { isSensitiveField } from './sensitive-filter.js';

const DEFAULT_LIMITS = {
  maxContentLength: 8000,   // chars of main text content
  maxCodeBlockLength: 600,  // chars per code block
  buttons: 20,
  links: 30,
  forms: 5,
  fieldsPerForm: 15,
  maxTextLength: 100,       // per interactive element label
};

// ─── Noise exclusion ────────────────────────────────────────────────────────

const NOISE_TAGS = new Set([
  'nav', 'header', 'footer', 'aside', 'script', 'style', 'noscript',
  'svg', 'canvas', 'iframe',
]);

const NOISE_ROLES = new Set([
  'navigation', 'banner', 'complementary', 'search',
]);

function isNoise(el) {
  if (NOISE_TAGS.has(el.tagName.toLowerCase())) return true;
  const role = el.getAttribute('role');
  if (role && NOISE_ROLES.has(role)) return true;
  const cls = el.className || '';
  // Common sidebar/nav class patterns
  if (/\b(sidebar|navbar|topbar|breadcrumb|cookie|toast|modal-backdrop)\b/i.test(cls)) return true;
  return false;
}

// ─── Visibility ──────────────────────────────────────────────────────────────

function isVisible(el) {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0' &&
    el.offsetWidth > 0 &&
    el.offsetHeight > 0
  );
}

function isInViewport(el, buffer = 500) {
  const rect = el.getBoundingClientRect();
  return (
    rect.bottom >= -buffer &&
    rect.top <= window.innerHeight + buffer &&
    rect.right >= 0 &&
    rect.left <= window.innerWidth
  );
}

// ─── Selector generation (kept for interactive elements) ─────────────────────

function isAutoGeneratedId(id) {
  return /^(:r[0-9a-z]+:|react-|ng-|vue-|ember[0-9]+|_[a-z0-9]{8,}|[a-f0-9]{8}-[a-f0-9]{4}|uid-|id[0-9]+$)/i.test(id);
}

function getMeaningfulClass(el) {
  const utilityPatterns = [
    /^(flex|grid|block|inline|hidden)$/,
    /^(m|p|w|h|min|max)-/,
    /^(text|font|bg|border)-/,
    /^(col|row)-/,
    /^(sm|md|lg|xl|2xl):/,
    /^hover:|focus:|active:/,
    /^[a-z]{1,3}-[0-9]+$/,
  ];
  const classes = Array.from(el.classList || []);
  const meaningful = classes.filter(
    cls => !utilityPatterns.some(p => p.test(cls)) && cls.length > 2 && cls.length < 30
  );
  return meaningful[0] || null;
}

function buildPathSelector(el, maxDepth = 3) {
  const path = [];
  let cur = el;
  let depth = 0;
  while (cur && cur !== document.body && depth < maxDepth) {
    const tag = cur.tagName.toLowerCase();
    if (cur.id && !isAutoGeneratedId(cur.id)) {
      path.unshift(`#${CSS.escape(cur.id)}`);
      break;
    }
    const cls = getMeaningfulClass(cur);
    if (cls) {
      path.unshift(`${tag}.${cls}`);
    } else {
      const siblings = cur.parentElement?.children;
      if (siblings && siblings.length > 1) {
        const idx = Array.from(siblings).indexOf(cur) + 1;
        path.unshift(`${tag}:nth-child(${idx})`);
      } else {
        path.unshift(tag);
      }
    }
    cur = cur.parentElement;
    depth++;
  }
  return path.join(' > ');
}

export function generateSelector(el) {
  if (!el || !el.tagName) return null;
  if (el.dataset?.testid) return `[data-testid="${el.dataset.testid}"]`;
  if (el.dataset?.cy) return `[data-cy="${el.dataset.cy}"]`;
  const aria = el.getAttribute('aria-label');
  if (aria && aria.length < 50) return `[aria-label="${aria.replace(/"/g, '\\"')}"]`;
  if (el.id && !isAutoGeneratedId(el.id)) return `#${CSS.escape(el.id)}`;
  if (el.name) return `${el.tagName.toLowerCase()}[name="${el.name}"]`;
  if (['BUTTON', 'A'].includes(el.tagName)) {
    const text = el.innerText?.trim();
    if (text && text.length < 30) {
      const tag = el.tagName.toLowerCase();
      const matches = document.querySelectorAll(tag);
      if (Array.from(matches).filter(e => e.innerText?.trim() === text).length === 1) {
        return `${tag}:contains("${text.replace(/"/g, '\\"')}")`;
      }
    }
  }
  return buildPathSelector(el);
}

// ─── Main content extraction ─────────────────────────────────────────────────

/**
 * Find the best container element that holds the page's primary content.
 * Tries progressively wider selectors; falls back to document.body.
 */
function findContentRoot() {
  const candidates = [
    'article',
    '[role="article"]',
    'main',
    '[role="main"]',
    '.prose',
    '.markdown-body',
    '.content',
    '#content',
    '#main-content',
    '#main',
  ];
  for (const sel of candidates) {
    try {
      const el = document.querySelector(sel);
      if (el && isVisible(el)) return el;
    } catch {}
  }
  return document.body;
}

/**
 * Walk the content root and emit structured content items.
 * Skips noise elements (nav, header, aside…).
 *
 * Returns an array of items:
 *   { type: 'heading', level: 'h2', text: '...' }
 *   { type: 'text', text: '...' }
 *   { type: 'code', lang: 'bash', text: '...' }
 *   { type: 'listItem', text: '...' }
 */
function walkContent(root, limits) {
  const items = [];
  let charCount = 0;

  const BLOCK_TAGS = new Set(['p', 'li', 'dt', 'dd', 'td', 'th', 'blockquote', 'figcaption']);
  const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

  function walk(node) {
    if (charCount >= limits.maxContentLength) return;
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();

    // Skip noise subtrees entirely
    if (isNoise(node)) return;
    if (!isVisible(node)) return;

    // Heading → new section marker
    if (HEADING_TAGS.has(tag)) {
      const text = (node.innerText || '').trim();
      if (text) {
        items.push({ type: 'heading', level: tag, text });
        charCount += text.length;
      }
      return; // don't recurse — heading text already captured
    }

    // Code block
    if (tag === 'pre') {
      const codeEl = node.querySelector('code');
      const raw = (codeEl || node).innerText || '';
      const text = raw.trim().substring(0, limits.maxCodeBlockLength);
      if (text) {
        const lang = (codeEl?.className || '').match(/language-(\w+)/)?.[1] || '';
        items.push({ type: 'code', lang, text });
        charCount += text.length;
      }
      return;
    }

    // Inline code (not inside pre)
    if (tag === 'code' && node.closest('pre') === null) {
      // Let the parent block pick it up via innerText
      return;
    }

    // Block-level text elements
    if (BLOCK_TAGS.has(tag)) {
      // Skip if nested inside another block (e.g. <p> inside <blockquote> handled by blockquote)
      const parentTag = node.parentElement?.tagName.toLowerCase();
      if (tag !== 'blockquote' && BLOCK_TAGS.has(parentTag) && parentTag !== 'li') return;

      const text = (node.innerText || '').trim();
      if (text) {
        const type = tag === 'li' ? 'listItem' : 'text';
        const truncated = text.substring(0, 400);
        items.push({ type, text: truncated });
        charCount += truncated.length;
      }
      return; // innerText already captures children
    }

    // Container — recurse into children
    for (const child of node.children) {
      if (charCount >= limits.maxContentLength) break;
      walk(child);
    }
  }

  walk(root);
  return items;
}

/**
 * Convert structured content items into a readable flat string,
 * organised by heading sections.
 */
function itemsToText(items) {
  let text = '';
  for (const item of items) {
    switch (item.type) {
      case 'heading':
        text += `\n${'#'.repeat(parseInt(item.level[1]))} ${item.text}\n`;
        break;
      case 'text':
        text += `${item.text}\n`;
        break;
      case 'listItem':
        text += `• ${item.text}\n`;
        break;
      case 'code':
        text += `\`\`\`${item.lang}\n${item.text}\n\`\`\`\n`;
        break;
    }
  }
  return text.trim();
}

/**
 * Extract the main readable content of the page.
 */
function extractMainContent(limits) {
  const root = findContentRoot();
  const items = walkContent(root, limits);
  const text = itemsToText(items);
  const truncated = text.length >= limits.maxContentLength;

  return {
    text,
    items,
    truncated,
    sourceElement:
      root.tagName.toLowerCase() +
      (root.id ? `#${root.id}` : '') +
      (getMeaningfulClass(root) ? `.${getMeaningfulClass(root)}` : ''),
  };
}

// ─── Interactive elements ─────────────────────────────────────────────────────

function extractButtons(limits) {
  const buttons = [];
  const els = document.querySelectorAll(
    'button, [type="submit"], [type="button"], [role="button"], .btn, .button'
  );
  for (const el of els) {
    if (buttons.length >= limits.buttons) break;
    if (!isVisible(el)) continue;
    const text = (el.innerText || el.value || el.getAttribute('aria-label') || '').trim();
    if (!text) continue;
    buttons.push({
      selector: generateSelector(el),
      text: text.substring(0, limits.maxTextLength),
      type: el.type || 'button',
      disabled: el.disabled || false,
      inViewport: isInViewport(el, 0),
    });
  }
  return buttons;
}

function extractLinks(limits) {
  const links = [];
  const els = document.querySelectorAll('a[href]');
  const origin = window.location.origin;
  for (const el of els) {
    if (links.length >= limits.links) break;
    if (!isVisible(el)) continue;
    const text = (el.innerText || el.getAttribute('aria-label') || '').trim();
    if (!text || text.length < 2) continue;
    const href = el.href;
    if (!href || href.startsWith('javascript:') || href === '#') continue;
    let internal = false;
    try { internal = new URL(href).origin === origin; } catch {}
    links.push({
      selector: generateSelector(el),
      text: text.substring(0, limits.maxTextLength),
      href,
      internal,
      inViewport: isInViewport(el, 0),
    });
  }
  return links;
}

function extractForms(limits) {
  const forms = [];
  let sensitiveCount = 0;
  for (const form of document.querySelectorAll('form')) {
    if (forms.length >= limits.forms) break;
    if (!isVisible(form)) continue;
    const fields = [];
    for (const field of form.querySelectorAll('input, textarea, select')) {
      if (fields.length >= limits.fieldsPerForm) break;
      if (field.type === 'hidden') continue;
      if (isSensitiveField(field)) { sensitiveCount++; continue; }
      let label = null;
      if (field.id) {
        const lEl = document.querySelector(`label[for="${field.id}"]`);
        label = lEl?.innerText?.trim();
      }
      label = label || field.getAttribute('aria-label') || field.placeholder || null;
      fields.push({
        selector: generateSelector(field),
        name: field.name || null,
        type: field.type || 'text',
        label: label?.substring(0, 50) || null,
        placeholder: field.placeholder?.substring(0, 50) || null,
        required: field.required || false,
      });
    }
    if (!fields.length) continue;
    forms.push({
      selector: generateSelector(form),
      id: form.id || null,
      action: form.action || null,
      method: (form.method || 'get').toUpperCase(),
      fields,
    });
  }
  return { forms, sensitiveCount };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Main extraction function — called by the read_page tool handler.
 *
 * Returns:
 *   content        — flat readable text of the page (most important)
 *   contentItems   — structured array of { type, text, level?, lang? }
 *   contentTruncated — true if content was cut at maxContentLength
 *   url, title, language, viewport
 *   buttons, links, forms  — interactive elements for navigation / actions
 *   metadata
 */
export async function extractDOMContext(options = {}) {
  const limits = { ...DEFAULT_LIMITS, ...options.limits };

  const mainContent = extractMainContent(limits);
  const buttons = extractButtons(limits);
  const links = extractLinks(limits);
  const { forms, sensitiveCount } = extractForms(limits);

  const htmlLang =
    document.documentElement.lang ||
    document.querySelector('html')?.getAttribute('lang') ||
    null;
  const language =
    htmlLang || navigator.language || (navigator.languages && navigator.languages[0]) || null;

  return {
    url: window.location.href,
    title: document.title,
    timestamp: Date.now(),
    language,

    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      pageHeight: document.documentElement.scrollHeight,
    },

    // ── Primary: actual page content ──────────────────────────────────────
    content: mainContent.text,
    contentItems: mainContent.items,
    contentTruncated: mainContent.truncated,
    contentSource: mainContent.sourceElement,

    // ── Secondary: interactive elements ───────────────────────────────────
    buttons,
    links,
    forms,

    metadata: {
      totalContentChars: mainContent.text.length,
      contentItemCount: mainContent.items.length,
      buttonsCount: buttons.length,
      linksCount: links.length,
      formsCount: forms.length,
      sensitiveFieldsFiltered: sensitiveCount,
    },
  };
}

/**
 * Minimal context snapshot — used on reconnect / navigation events.
 */
export async function extractMinimalContext() {
  const htmlLang =
    document.documentElement.lang ||
    document.querySelector('html')?.getAttribute('lang') ||
    null;
  const language =
    htmlLang || navigator.language || (navigator.languages && navigator.languages[0]) || null;
  return {
    url: window.location.href,
    title: document.title,
    timestamp: Date.now(),
    language,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollY: window.scrollY,
    },
  };
}

export default {
  extractDOMContext,
  extractMinimalContext,
  generateSelector,
  DEFAULT_LIMITS,
};
